import { useEffect } from 'react'
import { Plane, Raycaster, Vector2, Vector3 } from 'three'
import { useThree } from '@react-three/fiber'
import { usePlanImageCardStore } from '@editorStores'

interface Props {
  enabled: boolean
  resolveZ: (point: Vector3) => number
}

/** Tracks the latest scene pointer position for clipboard image paste placement. */
const ScenePointerTracker = ({ enabled, resolveZ }: Props): null => {
  const setLastScenePointer = usePlanImageCardStore(state => state.setLastScenePointer)
  const { camera, gl } = useThree()

  useEffect(() => {
    if (!enabled) return

    const canvas = gl.domElement
    const raycaster = new Raycaster()
    const pointer = new Vector2()

    const handlePointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
      raycaster.setFromCamera(pointer, camera)
      const planeZ = resolveZ(new Vector3(0, 0, 0))
      const plane = new Plane(new Vector3(0, 0, 1), -planeZ)
      const intersection = new Vector3()
      const hit = raycaster.ray.intersectPlane(plane, intersection)
      if (!hit) return
      setLastScenePointer({ x: intersection.x, y: intersection.y, z: planeZ })
    }

    canvas.addEventListener('pointermove', handlePointerMove)
    return () => canvas.removeEventListener('pointermove', handlePointerMove)
  }, [camera, enabled, gl, resolveZ, setLastScenePointer])

  return null
}

export default ScenePointerTracker
